import { __assign } from "tslib";
import { Check16 } from '@yunex/yds-icons';
import { ActionGroup, Button, Divider, ToggleButton, ActionGroupItemVisibility as Visibility, rem, } from '@yunex/yds-react';
import React, { useCallback, useEffect, useMemo, useState, } from 'react';
import { t } from '../../../localization/getTranslation';
import { CMP_SAVED_VIEWS_FILTER as CMP } from '../../componentNames';
import { useListViewStoreGetter, useListViewStoreSetter } from '../useListView';
import AddViewDialog from './AddViewDialog';
import { EditViewsDialog } from './EditViewsDialog';
import { UNDEFINED_FILTER_MODEL } from './ListTable';
// Styles.
var styles = {
    toggleButton: {
        height: rem(48),
        minWidth: rem(50),
    },
};
// Divider.
var divider = {
    el: React.createElement(Divider, { flexItem: true, orientation: "horizontal" }),
    key: 'viewsDivider',
    visibility: Visibility.HIDDEN,
};
var SavedViewsFilter = function (_a) {
    var _b, _c;
    var addViewLabel = _a.addViewLabel, addViewDialogProps = _a.addViewDialogProps, currentFilterModel = _a.currentFilterModel, editViewsLabel = _a.editViewsLabel, editViewsDialogProps = _a.editViewsDialogProps, _d = _a.hasAddView, hasAddView = _d === void 0 ? false : _d, _e = _a.hasEditViews, hasEditViews = _e === void 0 ? false : _e, _f = _a.hasSaveView, hasSaveView = _f === void 0 ? false : _f, onAddView = _a.onAddView, onEditViewsClick = _a.onEditViewsClick, onChangeSavedView = _a.onChangeSavedView, onSaveView = _a.onSaveView, _g = _a.savedViewItems, savedViewItems = _g === void 0 ? [] : _g, saveViewLabel = _a.saveViewLabel, sx = _a.sx, disabled = _a.disabled, filteredRowsCount = _a.filteredRowsCount;
    // We want to provide translations, but we also want to keep users to provide their own translations.
    var addViewLabelTrans = addViewLabel || t('addViewLabel');
    var editViewsLabelTrans = editViewsLabel || t('viewsOoo');
    var saveViewLabelTrans = saveViewLabel || t('saveLabel');
    var transLabels = {
        // Shared translation for both modals.
        addButtonLabel: (addViewDialogProps === null || addViewDialogProps === void 0 ? void 0 : addViewDialogProps.addButtonLabel) || t('addLabel'),
        cancelButtonLabel: (addViewDialogProps === null || addViewDialogProps === void 0 ? void 0 : addViewDialogProps.cancelButtonLabel) || t('cancelLabel'),
        inputErrorMessage: (addViewDialogProps === null || addViewDialogProps === void 0 ? void 0 : addViewDialogProps.inputErrorMessage) || t('fieldRequiredError'),
        inputLabel: (addViewDialogProps === null || addViewDialogProps === void 0 ? void 0 : addViewDialogProps.inputLabel) || t('viewNameLabel'),
        inputPlaceholder: (addViewDialogProps === null || addViewDialogProps === void 0 ? void 0 : addViewDialogProps.inputPlaceholder) || t('addViewPlaceholder'),
    };
    var gridApi = useListViewStoreGetter(function (s) { return s.gridApi; });
    // TODO
    // We need this value only when saving/updating therefore we can store it in getter.
    var autoColumnWidth = useListViewStoreGetter(function (s) { return s.autoColumnWidth; });
    var gridColumnApi = useListViewStoreGetter(function (s) { return s.gridColumnApi; });
    var quickSearch = useListViewStoreGetter(function (s) { return s.quickSearch; });
    var setListViewState = useListViewStoreSetter();
    var _h = useState((_b = currentFilterModel === null || currentFilterModel === void 0 ? void 0 : currentFilterModel.key) !== null && _b !== void 0 ? _b : 'all'), filterView = _h[0], setFilterView = _h[1];
    var _j = useState((_c = currentFilterModel === null || currentFilterModel === void 0 ? void 0 : currentFilterModel.key) !== null && _c !== void 0 ? _c : 'all'), lastSelectedView = _j[0], setLastSelectedView = _j[1];
    var _k = useState(false), isAddViewModalOpened = _k[0], setIsAddViewModalOpened = _k[1];
    var getNewViewData = useCallback(function (name) {
        var columnState = gridColumnApi === null || gridColumnApi === void 0 ? void 0 : gridColumnApi.getColumnState();
        if (currentFilterModel || columnState) {
            return {
                autoColumnWidth: autoColumnWidth,
                columnState: columnState,
                filters: currentFilterModel ? currentFilterModel.filters : {},
                key: name,
            };
        }
        else {
            console.error('No filter model or table column state to create a new view');
            return null;
        }
    }, [currentFilterModel, autoColumnWidth, gridColumnApi != undefined]);
    var createNewView = useCallback(function (name) {
        var newViewData = getNewViewData(name);
        if (!newViewData) {
            return;
        }
        var itemsCountWithoutQf = filteredRowsCount;
        if (gridApi === null || gridApi === void 0 ? void 0 : gridApi.isQuickFilterPresent()) {
            gridApi === null || gridApi === void 0 ? void 0 : gridApi.setQuickFilter('');
            itemsCountWithoutQf = gridApi.getDisplayedRowCount();
            gridApi === null || gridApi === void 0 ? void 0 : gridApi.setQuickFilter(quickSearch !== null && quickSearch !== void 0 ? quickSearch : '');
        }
        setFilterView(name);
        setLastSelectedView(name);
        onAddView === null || onAddView === void 0 ? void 0 : onAddView(name, newViewData, itemsCountWithoutQf);
    }, [getNewViewData, onAddView]);
    // After user changes something manually in the table filters, currentFilterModel.key is set to UNDEFINED_FILTER_MODEL. Thanks to this we can switch off the saved view filter.
    useEffect(function () {
        if ((currentFilterModel === null || currentFilterModel === void 0 ? void 0 : currentFilterModel.key) === UNDEFINED_FILTER_MODEL) {
            setFilterView('');
        }
        if (currentFilterModel && (currentFilterModel === null || currentFilterModel === void 0 ? void 0 : currentFilterModel.key) !== UNDEFINED_FILTER_MODEL) {
            setLastSelectedView(currentFilterModel.key);
        }
    }, [currentFilterModel === null || currentFilterModel === void 0 ? void 0 : currentFilterModel.key]);
    var tableColumnState = gridColumnApi === null || gridColumnApi === void 0 ? void 0 : gridColumnApi.getColumnState();
    useEffect(function () {
        var _a;
        var tableColumnStateFromView = (_a = savedViewItems.find(function (view) { return view.savedViewDataModel.key === filterView; })) === null || _a === void 0 ? void 0 : _a.savedViewDataModel.columnState;
        if (tableColumnStateFromView &&
            JSON.stringify(tableColumnState) !== JSON.stringify(tableColumnStateFromView)) {
            setFilterView('');
        }
    }, [JSON.stringify(tableColumnState), savedViewItems]);
    var handleViewSelect = useCallback(function (savedViewModel) { return function () {
        onChangeSavedView === null || onChangeSavedView === void 0 ? void 0 : onChangeSavedView(savedViewModel);
        setFilterView(savedViewModel.key);
        if (typeof (savedViewModel === null || savedViewModel === void 0 ? void 0 : savedViewModel.autoColumnWidth) === 'boolean' && setListViewState) {
            setListViewState({ autoColumnWidth: savedViewModel.autoColumnWidth });
        }
        if (savedViewModel === null || savedViewModel === void 0 ? void 0 : savedViewModel.columnState) {
            setListViewState({ columnState: savedViewModel.columnState });
        }
    }; }, [onChangeSavedView]);
    var overrideViewWithCurrentSettings = useCallback(function (name) { return function () {
        var columnState = gridColumnApi === null || gridColumnApi === void 0 ? void 0 : gridColumnApi.getColumnState();
        if (currentFilterModel || columnState) {
            var newViewData = {
                autoColumnWidth: autoColumnWidth,
                columnState: columnState,
                filters: currentFilterModel ? currentFilterModel.filters : {},
                key: name,
            };
            onSaveView === null || onSaveView === void 0 ? void 0 : onSaveView(name, newViewData, filteredRowsCount);
            setLastSelectedView(name);
            setFilterView(name);
        }
        else {
            console.error('No filter model or table column state to create a new view');
        }
    }; }, [
        autoColumnWidth,
        onSaveView,
        setFilterView,
        currentFilterModel,
        filteredRowsCount,
        gridColumnApi != undefined,
    ]);
    var saveCurrentView = useMemo(function () {
        if (!hasSaveView) {
            return undefined;
        }
        var foundView = savedViewItems.find(function (view) { return view.savedViewDataModel.key === lastSelectedView; });
        var viewLabel = (foundView === null || foundView === void 0 ? void 0 : foundView.caption) || (foundView === null || foundView === void 0 ? void 0 : foundView.innerContent);
        if (!viewLabel) {
            return undefined;
        }
        return {
            el: (React.createElement(Button, { "aria-label": lastSelectedView, "data-testid": "".concat(CMP, "-saveCurrentView-button"), onClick: overrideViewWithCurrentSettings(lastSelectedView) },
                "Save \"",
                viewLabel,
                "\"")),
            key: 'saveCurrentView',
            label: "".concat(saveViewLabelTrans, " \"").concat(viewLabel, "\""),
            visibility: Visibility.HIDDEN,
        };
    }, [lastSelectedView, overrideViewWithCurrentSettings, savedViewItems, saveViewLabelTrans]);
    var addView = useMemo(function () {
        if (!hasAddView) {
            return undefined;
        }
        if (hasAddView && !addViewDialogProps) {
            throw new Error("[YDS-REACT-COMPOSITES]: addViewDialogProps has to be defined for addViewDialog");
        }
        return {
            el: (React.createElement(Button, { "aria-label": addViewLabelTrans, color: "secondary", "data-testid": "".concat(CMP, "-addView-button"), onClick: function () { return setIsAddViewModalOpened(true); } }, addViewLabelTrans)),
            key: 'addView',
            label: addViewLabelTrans,
            visibility: Visibility.HIDDEN,
        };
    }, [hasAddView, addViewLabelTrans, onAddView]);
    var editViewsModal = useMemo(function () {
        if (!hasEditViews || savedViewItems.length === 0) {
            return undefined;
        }
        return {
            el: (React.createElement(Button, { "aria-label": editViewsLabelTrans, "data-testid": "".concat(CMP, "-editView-button"), onClick: onEditViewsClick }, editViewsLabelTrans)),
            key: 'editView',
            label: editViewsLabelTrans,
            visibility: Visibility.HIDDEN,
        };
    }, [hasEditViews, editViewsLabelTrans, onEditViewsClick, savedViewItems.length === 0]);
    var els = useMemo(function () {
        var items = savedViewItems.reduce(function (prev, item) {
            if (!item.isActive) {
                return prev;
            }
            var caption = item.caption, icon = item.icon, innerContent = item.innerContent, savedViewDataModel = item.savedViewDataModel, _a = item.size, size = _a === void 0 ? 'large' : _a, visibility = item.visibility;
            var key = savedViewDataModel.key;
            var isSelected = filterView === key;
            prev.push({
                el: (React.createElement(ToggleButton, { "aria-label": caption, 
                    // If inner content is defined, use caption here.
                    caption: innerContent != undefined ? caption : '', "data-testid": "".concat(CMP, "-").concat(key, "-toggleButton"), disabled: disabled, key: key, onChange: handleViewSelect(savedViewDataModel), selected: isSelected, size: size, startIcon: icon, sx: styles.toggleButton, value: isSelected, variant: "framed" }, innerContent !== null && innerContent !== void 0 ? innerContent : caption)),
                label: caption !== null && caption !== void 0 ? caption : innerContent === null || innerContent === void 0 ? void 0 : innerContent.toString(),
                visibility: visibility,
                MenuItemProps: {
                    selectedItemIcon: React.createElement(Check16, null),
                },
            });
            return prev;
        }, []);
        items.push(divider);
        if (saveCurrentView && lastSelectedView && lastSelectedView !== UNDEFINED_FILTER_MODEL) {
            items.push(saveCurrentView);
        }
        if (addView) {
            items.push(addView);
        }
        if (editViewsModal) {
            items.push(editViewsModal);
        }
        return items;
    }, [
        addView,
        editViewsModal,
        filterView,
        handleViewSelect,
        lastSelectedView,
        saveCurrentView,
        savedViewItems,
        disabled,
    ]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ActionGroup, { "data-testid": "".concat(CMP, "-savedViews-actionGroup"), items: els.filter(Boolean), MenuProps: {
                menuButtonProps: {
                    disabled: disabled,
                },
            }, sx: __assign({ flex: 1 }, (sx || {})) }),
        addViewDialogProps && (React.createElement(AddViewDialog, __assign({}, transLabels, { handleAdd: createNewView, handleClose: function () { return setIsAddViewModalOpened(false); }, open: isAddViewModalOpened }))),
        hasEditViews && (editViewsDialogProps === null || editViewsDialogProps === void 0 ? void 0 : editViewsDialogProps.open) && (React.createElement(EditViewsDialog, __assign({}, editViewsDialogProps, { savedViewItems: savedViewItems || [] })))));
};
export default SavedViewsFilter;
//# sourceMappingURL=SavedViewsFilter.js.map